import { differenceInCalendarDays, format, formatDistanceToNowStrict, parseISO } from 'date-fns';

/**
 * Display formatting.
 *
 * Australia only, by decision (spec §18): DD/MM/YYYY throughout. The API always speaks
 * ISO-8601; nothing in this file ever travels back to the server.
 */

export function auDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'dd/MM/yyyy');
}

/**
 * A CALENDAR date — a certificate date, a seal's validity window. Read in UTC, because
 * that is how it is stored and "23 September" does not become "22 September" for a reader
 * in another timezone. Use `auDate` for an instant such as when something was received.
 */
export function auCalendarDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getUTCFullYear()}`;
}

export function auDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'dd/MM/yyyy HH:mm');
}

/** "3 days ago". Used in activity feeds, never where an exact date matters. */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return `${formatDistanceToNowStrict(date)} ago`;
}

export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? null : differenceInCalendarDays(date, new Date());
}

export function money(value: string | number | null | undefined, currency = 'AUD'): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return `${currency} ${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function quantity(value: string | number | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return `${n.toLocaleString('en-AU', { maximumFractionDigits: 3 })}${unit ? ` ${unit}` : ''}`;
}

/** Turns SCREAMING_SNAKE into Sentence case for a label. */
export function humanise(value: string | null | undefined): string {
  if (!value) return '—';
  const lower = value.toLowerCase().replace(/_/g, ' ');
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function bytes(value: number | null | undefined): string {
  if (!value) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * ISO 3166 alpha-2 to a readable name, for the countries that actually appear in
 * Australian trade documentation.
 *
 * A full 249-entry table would be dead weight; `Intl.DisplayNames` covers the rest at
 * runtime with no bundle cost, and the raw code is a reasonable fallback — a customs
 * officer reads "VN" perfectly well.
 */
const displayNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en-AU'], { type: 'region' })
    : null;

export function country(code: string | null | undefined): string {
  if (!code) return '—';
  const upper = code.toUpperCase();
  try {
    return displayNames?.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

export function countryWithCode(code: string | null | undefined): string {
  if (!code) return '—';
  const name = country(code);
  return name === code.toUpperCase() ? name : `${name} (${code.toUpperCase()})`;
}
